<template>
	<v-dialog v-model="visible" max-width="360">
		<template v-slot:activator="{ on, attrs }">
			<slot v-bind="{ on, attrs }" />
		</template>

		<v-card :loading="loading">
			<layout-error-boundary
				ref="errBoundary"
				v-slot="{ hasError, err }"
				:on-error="handleFormError"
			>
				<v-form ref="form" lazy-validation @submit.prevent="submit">
					<v-card-title class="headline">{{
						strings.dialogs.addDevice.title
					}}</v-card-title>
					<v-card-text>
						<v-alert
							v-if="hasError"
							type="error"
							border="left"
							dismissible
							outlined
							@click.native="$refs.errBoundary.resetError()"
						>
							<span class="error--text">{{
								`${strings.dialogs.addDevice.error}${
									err.message ? ": " + err.message : ""
								}`
							}}</span>
						</v-alert>

						<v-select
							v-model="group"
							:rules="groupRules"
							:items="groups"
							label="Select group"
							clearable
						/>

						<v-select
							v-model="description"
							:disabled="descriptionDisabled"
							:rules="descriptionRules"
							:items="descriptions"
							label="Select device"
							item-text="name"
							item-value="data"
							clearable
						/>

						<v-select
							v-model="roomGuid"
							:rules="roomGuidRules"
							:items="roomListItems"
							label="Select Room"
						/>

						<v-text-field
							v-model="name"
							:rules="nameRules"
							label="Device name"
							type="text"
							clearable
						/>
					</v-card-text>

					<v-card-actions>
						<v-spacer />
						<v-btn color="rust-red" text @click="visible = false">Cancel</v-btn>
						<v-btn color="ocean darken-1" type="submit" outlined
							>Add Device</v-btn
						>
					</v-card-actions>
				</v-form>
			</layout-error-boundary>
		</v-card>
	</v-dialog>
</template>

<script>
import _ from "lodash";
import { mapActions, mapGetters } from "vuex";

import { NodeOperationError } from "@/utils/k4util";
import { APIError, APIAuthError } from "@/utils/http";
import strings from "@/utils/strings";

const initData = () => ({
	strings,
	visible: false,
	loading: false,

	resolve: null,
	handleSubmit: null,

	group: null,
	groupRules: [v => !!v || "You must select a group"],
	description: null,
	descriptionRules: [v => !!v || "You must select a device"],
	roomGuid: null,
	roomGuidRules: [v => !!v || "A destination room is required"],
	name: null,
	nameRules: [
		v => !!_.trim(v) || "Device name is required",
		v =>
			(_.isString(v) && v.indexOf("/")) === -1 ||
			"Device name must not contain '/' character",
	],
});

export default {
	props: {
		rooms: {
			type: Array,
			required: true,
		},
	},

	data: initData,

	computed: {
		...mapGetters({
			$groups: "descriptions/groups",
			descriptions: "descriptions/filteredByGroup",

			itemByPath: "devices/itemByPath",
			rootNode: "devices/rootNode",
		}),

		groups() {
			// Remove 'room' from groups
			return _.reject(this.$groups, group => group.value === "rooms");
		},

		roomListItems() {
			return _.map(this.rooms, room => ({
				text: room.name,
				value: room.meta ? room.meta.guid : room.guid,
			}));
		},

		descriptionDisabled() {
			return !this.group;
		},
	},

	watch: {
		visible(is, was) {
			// Reset all relevant data whenever dialog closes
			if (was && !is) this.reset();
		},

		group(val) {
			this.description = null;
			this.filterDescriptions(val);
		},

		description(val) {
			if (val && !this.name) {
				this.name = val.meta.description;
			}
		},
	},

	async beforeMount() {
		this.loading = true;
		try {
			// Load descriptions
			await this.$store.dispatch("descriptions/load");
		} finally {
			this.loading = false;
		}
	},

	methods: {
		...mapActions({
			filterDescriptions: "descriptions/filter",
		}),

		reset() {
			Object.assign(this, initData());
			this.$refs.errBoundary.resetError();
			this.$refs.form.resetValidation();
		},

		pop(handleSubmit) {
			this.visible = true;
			this.handleSubmit = handleSubmit;
			return new Promise(resolve => {
				this.resolve = resolve;
			});
		},

		async submit() {
			const valid = this.$refs.form.validate();
			if (!valid) return;

			this.loading = true;

			const formPayload = {
				name: this.name,
				parent: this.roomGuid,
				description: this.description,
			};
			try {
				let resp = formPayload;
				if (this.handleSubmit) {
					resp = await this.handleSubmit(formPayload);
				}
				if (this.resolve) {
					this.resolve(resp);
				}
				this.visible = false;
			} finally {
				this.loading = false;
			}
		},

		handleFormError(err) {
			// We only want to handle certain types of errors. Keep propagating
			// any others.
			const isApiErr =
				err instanceof APIError && !(err instanceof APIAuthError);
			const isNodeOpErr = err instanceof NodeOperationError;
			return !(isApiErr || isNodeOpErr);
		},
	},
};
</script>
